import React, { useState, useMemo } from 'react'
import styled from 'styled-components'

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: ${props => props.open ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const Modal = styled.div`
  width: 560px;
  max-width: 92vw;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.25);
  overflow: hidden;
`

const Header = styled.div`
  padding: 14px 16px;
  font-weight: 600;
  border-bottom: 1px solid #eee;
`

const Body = styled.div`
  padding: 16px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 16px;
`

const Footer = styled.div`
  padding: 12px 16px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  border-top: 1px solid #eee;
`

const Label = styled.label`
  font-size: 12px;
  color: #444;
  margin-bottom: 6px;
  display: block;
`

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
`

const TextArea = styled.textarea`
  grid-column: 1 / -1;
  width: 100%;
  min-height: 80px;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  resize: vertical;
`

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
`

const Button = styled.button`
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid #ddd;
  background: ${p => p.variant === 'primary' ? '#2563eb' : '#fff'};
  color: ${p => p.variant === 'primary' ? '#fff' : '#333'};
  font-weight: 600;
  cursor: pointer;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`

export default function AddObjectModal({ open, onClose, subcategories = [], defaultSubcategoryId = null, onCreated, categoryName = '', subcategoryDescription = '', categoryId = null }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [modelUrl, setModelUrl] = useState('')
  const [displayOrder, setDisplayOrder] = useState('')
  const [subcategoryId, setSubcategoryId] = useState(defaultSubcategoryId || '')
  const [saving, setSaving] = useState(false)
  const canSubmit = name.trim().length > 0 && (!saving)

  React.useEffect(() => {
    setSubcategoryId(defaultSubcategoryId || '')
  }, [defaultSubcategoryId, open])

  const orderedSubcategories = useMemo(() => {
    return [...subcategories].sort((a,b) => (a.display_order||0) - (b.display_order||0))
  }, [subcategories])

  const handleSubmit = async () => {
    if (!canSubmit) return
    setSaving(true)
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        image_url: imageUrl.trim() || null,
        model_url: modelUrl.trim() || null,
        display_order: displayOrder ? Number(displayOrder) : null,
      }
      let url = ''
      const rawId = String(subcategoryId || '')
      const numericId = Number(rawId)
      const hasNumericId = rawId.length > 0 && Number.isFinite(numericId)

      if (hasNumericId) {
        url = `/categories/subcategory/${numericId}/objects`
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        })
        const data = await res.json().catch(() => ({}))
        if (res.ok && data.success) {
          onCreated && onCreated(data.object)
        } else {
          alert(data.error || '오브젝트 추가 실패')
        }
        setSaving(false)
        return
      } else {
        // Try name + subcategory first
        let effectiveCategory = categoryName
        let effectiveDesc = subcategoryDescription
        if (!effectiveDesc && rawId) {
          const match = subcategories.find(sc => String(sc.description) === rawId || String(sc.name) === rawId)
          if (match) effectiveDesc = match.description || match.name
        }
        let triedNameSub = false
        if (effectiveCategory && effectiveDesc) {
          triedNameSub = true
          url = `/categories/name/${encodeURIComponent(effectiveCategory)}/subcategory/${encodeURIComponent(effectiveDesc)}/objects`
          const res1 = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          })
          const data1 = await res1.json().catch(() => ({}))
          if (res1.ok && data1.success) {
            onCreated && onCreated(data1.object)
            setSaving(false)
            return
          }
          // If 404 fallback to category-only
          if (res1.status !== 404) {
            alert(data1.error || '오브젝트 추가 실패')
            setSaving(false)
            return
          }
        }
        if (categoryId) {
          // Strongest fallback: numeric categoryId
          url = `/categories/${encodeURIComponent(categoryId)}/objects`
          const resId = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          })
          const dataId = await resId.json().catch(() => ({}))
          if (resId.ok && dataId.success) {
            onCreated && onCreated(dataId.object)
          } else {
            alert(dataId.error || '오브젝트 추가 실패')
          }
          setSaving(false)
          return
        } else if (effectiveCategory) {
          url = `/categories/name/${encodeURIComponent(effectiveCategory)}/objects`
          const res2 = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          })
          const data2 = await res2.json().catch(() => ({}))
          if (res2.ok && data2.success) {
            onCreated && onCreated(data2.object)
          } else {
            alert(data2.error || (triedNameSub ? '카테고리 전용 경로도 실패했습니다' : '오브젝트 추가 실패'))
          }
          setSaving(false)
          return
        }
        alert('서브카테고리를 선택하거나, 상단에서 카테고리를 먼저 선택하세요.')
        setSaving(false)
        return
      }
    } catch (e) {
      alert('오브젝트 추가 중 오류가 발생했습니다')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Backdrop open={open} onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose() }}>
      <Modal role="dialog" aria-modal="true">
        <Header>오브젝트 추가</Header>
        <Body>
          <div>
            <Label>서브카테고리</Label>
            <Select value={subcategoryId} onChange={(e) => setSubcategoryId(e.target.value)}>
              <option value="" disabled>선택</option>
              {orderedSubcategories.map(sc => (
                <option key={sc.subcategory_id || sc.description} value={sc.subcategory_id}>
                  {sc.description || sc.name}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label>이름</Label>
            <Input value={name} onChange={(e)=>setName(e.target.value)} placeholder="예: 의자" />
          </div>
          <div>
            <Label>이미지 URL</Label>
            <Input value={imageUrl} onChange={(e)=>setImageUrl(e.target.value)} placeholder="/obj/18.png 또는 /assets/..." />
          </div>
          <div>
            <Label>모델 URL (선택)</Label>
            <Input value={modelUrl} onChange={(e)=>setModelUrl(e.target.value)} placeholder="예: s3://... 또는 /models/..." />
          </div>
          <div>
            <Label>표시 순서 (선택)</Label>
            <Input type="number" inputMode="numeric" value={displayOrder} onChange={(e)=>setDisplayOrder(e.target.value)} placeholder="비우면 자동" />
          </div>
          <TextArea value={description} onChange={(e)=>setDescription(e.target.value)} placeholder="설명(선택)" />
        </Body>
        <Footer>
          <Button onClick={onClose} disabled={saving}>취소</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit}>{saving ? '저장 중...' : '추가'}</Button>
        </Footer>
      </Modal>
    </Backdrop>
  )
}
